import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Dropdown from "react-bootstrap/Dropdown";
import { Container, Modal } from "react-bootstrap";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  GetToken,
  RemovRoles,
  RemoveRefreshToken,
  RemoveToken,
  forceLogout,
  sameEmail,
} from "../../utils/helper/helper";
import {
  dismissAuctionReminder,
  formatBadgeCount,
  managerNotificationLink,
  onBellChanged,
  onNotificationsSeen,
  readAuctionReminders,
  receiveBellNotification,
  saveAuctionReminders,
  systemNotificationLink,
} from "../../utils/helper/notifications";
import dayjs from "dayjs";
import { revokeSession } from "../../utils/config/apiClient";
import { clearCheckoutIntent } from "../../utils/helper/checkout";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import { ImageUrl } from "../../utils/config/config";
import * as icons from "react-bootstrap-icons";
import { GetUnreadNotifications } from "../../redux/auth-reducer/auth-service";
import { useHubEvent } from "../../hooks/useHubEvent";
import { useHubResync } from "../../hooks/useHubResync";
import { useDispatch } from "react-redux";
import { setAuthToeken } from "../../redux/auth-reducer/auth-reducer";
import { Loader } from "rsuite";
import LiveFeedBanner from "../LiveFeedBanner/LiveFeedBanner";
import EmailVerificationBanner from "../EmailVerificationBanner/EmailVerificationBanner";
import { BidNoticePopup } from "../BidNotice/BidNoticePopup.jsx";
import { showBidNotice } from "../BidNotice/bidNoticeStore";
import {
  bellItemText,
  isBidNotification,
  playerNotificationLink,
} from "../../utils/helper/bidNotifications";

/** The bell with nothing unread. `items` is the bells' list: the Manager's and Admin's (#218), the Player's (#238). */
const NO_UNREAD = { unreadCount: 0, latestMessage: null, items: [] };

function TopNavbar({ sidebarfun }) {
  const roles = localStorage.getItem("role");
  // #218: the bells whose count is the server's - the Player's since #196, the Manager's and its
  // Sub-Admin's (role "Admin") since #218. The Web Admin's bell shows Contact Us queries.
  const hasServerBell = roles === "Player" || roles === "Manager" || roles === "Admin";
  const [showQuery, setShowQuery] = useState(false);
  const [signalRQuery, setSignalRQuery] = useState([]);
  const [queryCount, setQueryCount] = useState();
  // #196: the Player bell. Its unread count comes from the server and its start reminders from
  // notifications.js, because this component remounts on every screen and keeps nothing itself.
  // #218: the Manager and Admin bells use the same count, and list the unread notifications.
  const [unread, setUnread] = useState(NO_UNREAD);
  // #227: the Player's system notifications - complete the profile, buy a subscription, pay a failed
  // renewal. Read with the count, but kept apart from it: opening the Notifications page empties
  // `unread`, and these last until the Player does the thing.
  const [systemItems, setSystemItems] = useState([]);
  const [reminders, setReminders] = useState(() => readAuctionReminders());
  const unreadRequestRef = useRef(0);
  const dispatch = useDispatch();
  const [dataItem, setDataItem] = useState();
  // This state is used for the multi-user login modal
  const [showModal, setShowModal] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  const timerIdRef = useRef(null);
  const logoutTimerRef = useRef(null);

  const navigate = useNavigate();

  // WEB-P1-161: THE IDLE WINDOW IS A FIXED CONSTANT AND IS NOT DERIVED FROM THE TOKEN. It used
  // to be state, seeded to 20 minutes and then immediately overwritten in the effect below with
  // `decoded.exp * 1000 - Date.now()` - the access token's REMAINING lifetime. The access token
  // lives 20 minutes, and this component is rendered by ~50 screens, so it unmounts and remounts
  // on every route change and re-armed the timer from whatever was left at that instant.
  // Navigating at minute 16-18 therefore armed a 2-4 MINUTE "inactivity" timeout on a session
  // the user was actively using, which is the self-logout QA measured and could not characterise.
  //
  // An expiring access token is not an idle user and must not be handled here at all: it is
  // apiClient.js's job, which renews it silently on the first 401 carrying Token-Expired.
  const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000;

  useEffect(() => {
    // WEB-P1-161: this effect no longer decodes the token or signs anyone out. It used to call
    // logout() when `decoded.exp` was already in the past - but an expired ACCESS token is the
    // ordinary, fully recoverable case, since the refresh token outlives it by ten minutes, so
    // that branch signed out exactly the users apiClient.js was about to renew. Publishing the
    // session to redux is all that is left, and that is what builds the SignalR connection.
    //
    // #197: a boolean, not the token. Context.js only asks whether there is a session (the token
    // itself comes from getAccessToken()), but it rebuilds the connection whenever authtoken
    // changes - and apiClient.js renews the token without telling redux, so the first screen
    // change after every renewal published a new string, tore the hub down and reconnected,
    // dropping whatever was pushed in between. Login.js sets it back to false on sign-in, so a new
    // account still gets a new connection.
    dispatch(setAuthToeken(Boolean(GetToken())));
  }, [dispatch]);

  // WEB-P1-161: THIS IS THE DELIBERATE SIGN-OUT ONLY - the Logout item in the profile menu.
  // Every AUTOMATIC exit now goes through endSession() below instead, so that a session the user
  // did not choose to end always says why and always lands on the sign-in screen.
  //
  // The trailing localStorage.clear() is gone. It ran after the eleven targeted removals above
  // it, so it was never what did the work, and it took unrelated state with it - the pending
  // checkout intent most of all, which is how a purchase interrupted by a sign-out disappeared.
  //
  // FE-05: revokeSession() ends the session on the server first (and removes the token pair), and
  // setAuthToeken(false) stops the SignalR connection - this is an in-app navigation, not a reload,
  // so the socket used to stay open after Logout.
  //
  // #222: it lands on the sign-in page, as every automatic exit does. It went to the public /Home.
  async function logout() {
    await revokeSession();
    dispatch(setAuthToeken(false));
    RemovRoles();
    localStorage.removeItem("details");
    localStorage.removeItem("isUser");
    localStorage.removeItem("img");
    clearCheckoutIntent();
    localStorage.removeItem("itemnav");
    localStorage.removeItem("sesstionID");
    localStorage.removeItem("instruction");
    navigate("/Login");
  }

  /**
   * Ends a session the user did not ask to end.
   *
   * WEB-P1-161: all three automatic exits - the inactivity timer, the eviction when this account
   * signs in somewhere else, and a missing user record - used to call logout(), which cleared
   * storage and dropped the user on public /Home with no explanation whatsoever. /Home does not
   * read SESSION_ENDED_KEY, so there was nowhere for a reason to even be shown. forceLogout is
   * the one routine the axios interceptor already uses: it clears the same keys, stashes the
   * sentence for the sign-in screen to toast on arrival, and navigates to /Login.
   *
   * FE-05: the session is revoked on the server too, so an idle sign-out really ends it - except
   * when `revoke` is false, for the eviction (see its caller).
   *
   * @param {string} message Why the session ended, shown on the sign-in screen.
   * @param {{revoke?: boolean}} [options]
   */
  async function endSession(message, { revoke = true } = {}) {
    if (revoke) await revokeSession();
    clearCheckoutIntent();
    localStorage.removeItem("details");
    localStorage.removeItem("img");
    localStorage.removeItem("itemnav");
    localStorage.removeItem("sesstionID");
    forceLogout(message);
  }

  async function clearCaches() {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  }

  useEffect(() => {
    // WEB-P1-161: clears, and deliberately does NOT navigate. This fires while the tab is being
    // torn down with the eviction modal up, so the point is only to not leave a live session
    // behind; a navigate() or a location.assign() at this stage either does nothing or fights
    // the unload, which is why it no longer goes through logout() or endSession().
    const handleBeforeUnload = (event) => {
      if (showModal) {
        RemoveToken();
        RemoveRefreshToken();
        RemovRoles();
        localStorage.removeItem("isUser");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [showModal]);

  const resetInactivityTimer = () => {
    if (timerIdRef.current) clearTimeout(timerIdRef.current);
    timerIdRef.current = setTimeout(() => {
      setShowTimeoutModal(true);
      logoutTimerRef.current = setTimeout(() => {
        // WEB-P1-161: ends through forceLogout, so the user is told why and arrives at /Login -
        // this used to drop them on /Home with no message. The "showTimeOutModal" localStorage
        // flag that used to be written here is gone with the effect that read it.
        endSession("You have been signed out after a period of inactivity.");
      }, 5000);
    }, INACTIVITY_TIMEOUT_MS);
  };

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll"];
    const eventHandler = () => {
      if (!showTimeoutModal) {
        resetInactivityTimer();
      }
    };

    events.forEach((event) => window.addEventListener(event, eventHandler));
    resetInactivityTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, eventHandler)
      );
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [showTimeoutModal]);

  const isUser = JSON.parse(localStorage.getItem("isUser"));
  const fullName = isUser?.fullName;
  const userEmail = isUser?.email;

  useEffect(() => {
    // WEB-P1-161: routed through forceLogout like the others. It cleared four keys by hand and
    // redirected with no explanation, and it used RemoveUser(), which removes "userData" - the
    // Remember-Me blob - rather than the "isUser" record this test actually reads.
    if (!fullName || fullName.trim() === "") {
      endSession("Your session has ended. Please sign in again.");
    }
  }, [fullName, navigate]);

  // FE-02: the API decides who receives this (dbo.fn_CanSeeNotification) - it used to go to every
  // connected user, so this filter was all that kept other people's notifications off screen. It
  // stays as a display rule only: never alert someone to their own post. #218: the rule that kept a
  // Player's notification for the practice manager alone is gone - a Player's subscription request
  // is addressed to its Manager, and the server sends it to nobody else.
  useHubEvent("ReceiveNotification", (data) => {
    if (!data?.message || data?.createdBy === isUser?.userId || !hasServerBell) return;
    // #196: one more unread on top of what the server counted when this screen opened. The count
    // used to live in localStorage "messageCount", which nothing ever displayed. #218: and the
    // notification itself at the top of the Manager and Admin bells' list. #218 review: not when
    // that count already included it (receiveBellNotification).
    setUnread((current) => receiveBellNotification(current, data));
  });
  // #238: a Player's outbid, won or lost notification. One more unread on the bell, as for
  // ReceiveNotification, and the pop-up on whatever page is open. Its own hub message because the
  // mobile app shows every ReceiveNotification verbatim and these are web-only.
  useHubEvent("ReceiveBidNotification", (data) => {
    if (roles !== "Player" || !isBidNotification(data)) return;
    setUnread((current) => receiveBellNotification(current, data));
    showBidNotice(data);
  });
  useHubEvent("ContactUs", (queryData) => {
    setSignalRQuery(queryData[0]);
    setQueryCount(queryData?.length);
  });

  // #196: every row is kept, with its auction id, so each reminder opens its own auction. This
  // used to keep the first row's sentence only, and chose the page to open by looking for the
  // words "secondary market" in it - which only an auction named that way could contain, since
  // the server sends no resale reminders. Players only: the link is a Player screen.
  useHubEvent("ReceiveAuctionNotification", (data) => {
    if (roles === "Player") {
      setReminders(saveAuctionReminders(data));
    }
  });

  // #196: the Player bell's count is the server's (Announcement/GetUnreadNotifications), read on
  // every screen because this component remounts on each one; it used to be whatever this browser
  // had received live, so every sign-in started at nothing. The Notifications page marks it all
  // read and announces that. The request number stops a reply that was already on its way from
  // putting the old count back afterwards. #218: the Manager and Admin bells read it too, with the
  // unread notifications themselves.
  const loadUnread = useCallback(() => {
    if (!hasServerBell) return;

    const request = ++unreadRequestRef.current;
    GetUnreadNotifications().then((res) => {
      const data = res?.data;
      if (request !== unreadRequestRef.current || typeof data?.unreadCount !== "number") return;
      setUnread({
        unreadCount: data.unreadCount,
        latestMessage: data.latestMessage ?? null,
        items: Array.isArray(data.items) ? data.items : [],
      });
      setSystemItems(Array.isArray(data.systemItems) ? data.systemItems : []);
    });
  }, [hasServerBell]);

  useEffect(() => {
    if (!hasServerBell) return undefined;

    loadUnread();

    const stopListening = onNotificationsSeen(() => {
      setUnread(NO_UNREAD);
      // #227: and read again - the page announces this once the server has moved the watermark, so
      // the read finds nothing unread. It used to only discard the read still on its way, so the old
      // count could not come back; a new read does that too (loadUnread numbers it), and it brings
      // back the system notifications the discarded one was carrying, which stay until done.
      loadUnread();
    });
    // #227: the profile was saved or a promo code redeemed on this screen - read the system
    // notifications again rather than leave one up that no longer applies.
    const stopListeningChanged = onBellChanged(loadUnread);

    return () => {
      stopListening();
      stopListeningChanged();
      unreadRequestRef.current += 1;
    };
  }, [hasServerBell, loadUnread]);

  // #218: what arrived while the connection was down is not replayed, so read it again.
  useHubResync(loadUnread);

  // #218: the subscription request a notification announced is no longer Pending - the Player
  // cancelled it, the Manager answered it (#218 review), or the Player's account was deleted - and
  // the server deleted the notification. Read the count and list again rather than guess whether it
  // was still unread.
  useHubEvent("RemoveNotification", () => {
    loadUnread();
  });

  // #227: the system notifications count too. The server sends them to Players only.
  const bellCount = unread.unreadCount + reminders.length + systemItems.length;
  const bellLabel = formatBadgeCount(bellCount);
  const managerBellLabel = formatBadgeCount(unread.unreadCount);

  /** "Spring Classic starts at 3:00 PM", from the start time, so it never goes stale. */
  const describeReminder = (reminder) => {
    const start = dayjs(reminder.startsAt);
    const when = start.isSame(dayjs(), "day")
      ? start.format("h:mm A")
      : start.format("MMM D, h:mm A");
    return `${reminder.auctionName || "An auction"} starts at ${when}`;
  };

  const handleReminderClick = (auctionId) => {
    localStorage.removeItem("itemnav");
    setReminders(dismissAuctionReminder(auctionId));
  };

  const handleLandingPage = () => {
    navigate("/");
    localStorage.removeItem("itemnav");
  };

  const handleQueryPage = () => {
    navigate("/WebAdmin/Query");
  };

  const handleQueryClick = () => {
    setShowQuery(!showQuery);
  };

  // #218: no navigate() here either - each entry of the Manager and Admin bells is a Link to where
  // that notification leads (managerNotificationLink).
  const handleNotificationPageManager = () => {
    localStorage.removeItem("itemnav");
  };

  // #196: no navigate() here - the Link already goes there. The old handler navigated to the
  // Notifications page as well, from the reminder's link too, pushing two history entries.
  const handleNotificationPagePlayer = () => {
    localStorage.removeItem("itemnav");
  };

  // FE-02: the API now sends this only to this account's own connections (Clients.User); it used to
  // reach every connected user, carrying every sign-in's email. The email check stays as a guard -
  // a server that still broadcasts must not sign out everyone on each sign-in - but compares the
  // way addresses match, not character for character: the payload is the address as typed.
  useHubEvent("ReceiveUserStatus", (data) => {
    setDataItem(data);
  });

  useEffect(() => {
    if (sameEmail(dataItem, isUser?.email)) {
      setShowModal(true);
    }
  }, [dataItem, isUser]);

  useEffect(() => {
    if (showModal) {
      const timer = setTimeout(() => {
        // WEB-P1-161: the eviction ends the session the same way every other automatic exit does,
        // so it says why and lands on /Login. It used to call logout() - cleared storage, public
        // /Home, no message - which is one of the ways a session could vanish mid-use with
        // nothing on screen to explain it.
        setShowModal(false);
        // FE-05: not revoked. The sign-in elsewhere already replaced this session on the server, and
        // when it happened in another tab of this browser the stored pair is already the NEW
        // session's - revoking it would sign that one out as well.
        endSession("You were signed out because this account signed in somewhere else.", {
          revoke: false,
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showModal]);

  // WEB-P1-161: the "modalShown" / "showTimeOutModal" localStorage flags and the effect that
  // bounced the next mount to /login on finding either are gone. They existed because the old
  // logout path navigated to /Home and could not carry the user to the sign-in screen itself, so
  // a flag was left behind for the next mount to finish the job - a second, message-less
  // redirect that fired on whatever route happened to mount next. forceLogout navigates on its
  // own, so there is nothing left to hand off.

  // #104: this used to fire a token refresh as well, unawaited and with its result ignored. Staying
  // signed in needs none - apiClient.js renews ahead of expiry and on any expired-token 401 - and a
  // refresh started from a component is one more rotation racing the ones the client serialises.
  const handleStayLoggedIn = () => {
    setShowTimeoutModal(false);
    resetInactivityTimer();
  };

  return (
    <>
      <div className="topbar">
        <Container fluid>
          {sameEmail(dataItem, isUser?.email) && (
            <Modal show={showModal} centered>
              <Modal.Header >
                <Modal.Title>
                  <b>New Session Detected</b>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Your session is logged out because a new session is created with
                the same credentials by another user. Please log in again to
                continue.
              </Modal.Body>
            </Modal>
          )}

          {/* Inactivity Timeout Modal */}
          <Modal show={showTimeoutModal} centered>
            <Modal.Header>
              <Modal.Title>
                <b>Inactivity Session Time-out</b>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              You have been inactive for a while. Your session will expire soon.
              Please click "Stay Logged In" if you wish to continue your
              session.
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={handleStayLoggedIn}>
                Stay Logged In
              </Button>
            </Modal.Footer>
          </Modal>
          <Row className="align-items-center ">
            <Col xs={6} className=" mob">
              <div className="logo-image">
                <Link to="/Home">
                  <img src="/image/logo1.png" width={115} alt="logo" />
                </Link>
              </div>
            </Col>
            <Col xs={6} className="text-right mob">
              <img
                src="/image/minu-icon.svg"
                onClick={sidebarfun}
                className="icon-menue"
                alt="menu icon"
              />
            </Col>
            <Col md="6" className="desk">
              <div className="innetr-group p-2">
                <div className="group-top-bar ms-0">
                  <div className="logo-image">
                    <Link to="/Home">
                      <img src="/image/logo1.png" width={115} alt="logo" />
                    </Link>
                  </div>
                  <div className="icon-bar">
                    <img
                      src="/image/minu-icon.svg"
                      onClick={sidebarfun}
                      className="icon-menue"
                      alt="menu icon"
                    />
                  </div>
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="d-flex p-2 align-items-center justify-content-end dropdown-section">
                <div className="me-3" onClick={handleLandingPage}>
                  <icons.HouseDoor className="home-icon-top" />
                </div>
                {roles === "Web Admin" ? (
                  <Dropdown
                    className="notification-message"
                    onClick={handleQueryClick}
                  >
                    <Dropdown.Toggle
                      variant="success"
                      id="dropdown-basic"
                      className="notification-icon"
                    >
                      <icons.Bell
                        onClick={handleQueryClick}
                        className="bell-notify"
                      />
                      {signalRQuery?.length ? (
                        <span
                          className="badge-notification"
                          onClick={handleQueryClick}
                        >
                          !
                        </span>
                      ) : null}
                    </Dropdown.Toggle>
                    {signalRQuery?.length === 0 ? (
                      <Dropdown.Menu>No Data Found</Dropdown.Menu>
                    ) : (
                      <Dropdown.Menu>
                        {showQuery && (
                          <div onClick={handleQueryPage}>
                            {signalRQuery?.message}
                          </div>
                        )}
                      </Dropdown.Menu>
                    )}
                  </Dropdown>
                ) : roles === "Manager" || roles === "Admin" ? (
                  // #196: Admin (a Manager's Sub-Admin) too, as in Sidebar.js. It used to fall
                  // through to the Player bell, which now shows only Player data.
                  // #218: the server's unread count, and each unread notification as its own
                  // entry - a Player's subscription request opens Players on its Pending tab. It
                  // used to show only the last live push, gone on the next screen. Opening the
                  // Notifications page marks them read, as for the Player.
                  <Dropdown className="notification-message">
                    <Dropdown.Toggle
                      variant="success"
                      id="dropdown-basic"
                      className="notification-icon"
                    >
                      <icons.Bell className="bell-notify" />
                      {unread.unreadCount > 0 ? (
                        <span
                          className="badge-notification"
                          style={managerBellLabel.length > 1 ? { fontSize: 13 } : undefined}
                        >
                          {managerBellLabel}
                        </span>
                      ) : null}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {unread.items.map((item) => {
                        const link = managerNotificationLink(item);
                        return (
                          <Link
                            key={item.id}
                            to={link.to}
                            state={link.state}
                            className="d-block mb-2"
                            onClick={handleNotificationPageManager}
                          >
                            {item.message}
                          </Link>
                        );
                      })}
                      {unread.unreadCount > unread.items.length ? (
                        <Link
                          to="/SuperAdmin/SuperNotification"
                          className="d-block mb-2"
                          onClick={handleNotificationPageManager}
                        >
                          {`View all notifications (+${unread.unreadCount - unread.items.length} more)`}
                        </Link>
                      ) : null}
                      {unread.unreadCount === 0 ? <p>No Data Found</p> : null}
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <Dropdown className="notification-message">
                    <Dropdown.Toggle
                      variant="success"
                      id="dropdown-basic"
                      className="notification-icon"
                    >
                      <icons.Bell className="bell-notify" />
                      {/* #196: one badge with the real count. There used to be two, each a
                          hard-coded "1", drawn on top of each other. */}
                      {bellCount > 0 ? (
                        <span
                          className="badge-notification"
                          style={bellLabel.length > 1 ? { fontSize: 13 } : undefined}
                        >
                          {bellLabel}
                        </span>
                      ) : null}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {/* #227: what the Player still has to do, first, each opening the page
                          where it is done. */}
                      {systemItems.map((item) => (
                        <Link
                          key={item.notificationType}
                          to={systemNotificationLink(item)}
                          className="d-block mb-2"
                          onClick={handleNotificationPagePlayer}
                        >
                          {item.message}
                        </Link>
                      ))}
                      {/* #238: each unread notification as its own entry, as on the Manager bell,
                          so an outbid, won or lost one opens its auction or the Bid History
                          (AC19); any other opens the Notifications page. Opening one does not
                          mark it read; the Notifications page does (AC18). */}
                      {unread.items.map((item) => {
                        const link = playerNotificationLink(item);
                        return (
                          <Link
                            key={item.id}
                            to={link.to}
                            state={link.state}
                            className="d-block mb-2"
                            onClick={handleNotificationPagePlayer}
                          >
                            {bellItemText(item)}
                          </Link>
                        );
                      })}
                      {unread.unreadCount > unread.items.length ? (
                        <Link
                          to="/Player/Notification"
                          className="d-block mb-2"
                          onClick={handleNotificationPagePlayer}
                        >
                          {`View all notifications (+${unread.unreadCount - unread.items.length} more)`}
                        </Link>
                      ) : null}
                      {reminders.map((reminder) => (
                        <Link
                          key={reminder.auctionId}
                          to="/Player/AuctionPlayer"
                          state={{
                            auctionId: reminder.auctionId,
                            auctionName: reminder.auctionName,
                          }}
                          className="d-block mb-2"
                          onClick={() => handleReminderClick(reminder.auctionId)}
                        >
                          {describeReminder(reminder)}
                        </Link>
                      ))}
                      {bellCount === 0 ? <p>No Data Found</p> : null}
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                {/* #227: any empty image, not only null - the photo is optional, and a profile saved
                    without one could hold "". */}
                {!isUser?.userImage ? (
                  <img
                    src="/image/profileDefault.png"
                    width="50"
                    className="rounded-circle me-3"
                    alt="profile"
                  />
                ) : (
                  <img
                    src={ImageUrl + isUser?.userImage}
                    className="profile-image"
                    alt="profile"
                  />
                )}
                <Dropdown>
                  {roles}
                  <Dropdown.Toggle
                    variant="none"
                    className="d-flex align-items-center p-0"
                  >
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="tooltip1">{fullName}</Tooltip>}
                    >
                      <div className="text-hide">
                        {isUser?.fullName === null ? <Loader /> : fullName}
                      </div>
                    </OverlayTrigger>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {roles === "Web Admin" ? (
                      <Dropdown>
                        <Link to="/WebAdmin/profile" className="item-link">
                          Profile
                        </Link>
                      </Dropdown>
                    ) : roles === "Super Admin" ? (
                      <Dropdown>
                        <Link className="item-link" to="/SuperAdmin/Profile">
                          Profile
                        </Link>
                      </Dropdown>
                    ) : (
                      <Dropdown>
                        <Link className="item-link" to="/Player/Profile">
                          Profile
                        </Link>
                      </Dropdown>
                    )}
                    {roles === "Web Admin" ? (
                      <Dropdown>
                        <Link
                          className="item-link"
                          to="/WebAdmin/ChangePassword"
                        >
                          Change Password
                        </Link>
                      </Dropdown>
                    ) : roles === "Super Admin" ? (
                      <Dropdown>
                        <Link
                          className="item-link"
                          to="/SuperAdmin/ChangePassword"
                        >
                          Change Password
                        </Link>
                      </Dropdown>
                    ) : (
                      <Dropdown>
                        <Link className="item-link" to="/Player/ChangePassword">
                          Change Password
                        </Link>
                      </Dropdown>
                    )}
                    <Dropdown.Item onClick={logout}>Logout</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      {/* #182: says when the live feed is down; TopNavbar is on every dashboard page. */}
      <LiveFeedBanner />
      {/* Asks an account whose email is not verified to verify it, on every dashboard page. */}
      <EmailVerificationBanner />
      {/* #238: the outbid / won / lost pop-up, on every Player page. */}
      {roles === "Player" ? <BidNoticePopup /> : null}
    </>
  );
}

export default TopNavbar;
